<template>
  <div class="app vld-parent">
    <notifications group="app" position="top center">
      <template slot="body" slot-scope="props">
        <b-alert :variant="props.item.type" show class="m-2 text-center">{{
          props.item.text
        }}</b-alert>
      </template>
    </notifications>
    <AppHeader fixed class="vertical">
      <SidebarToggler
        v-if="layout.sidebar"
        class="d-lg-none"
        display="sm"
        :default-open="false"
        mobile
      />
      <b-link
        v-if="(minimized && logoMinimized) || (!minimized && logo)"
        class="navbar-brand"
        to="#"
      >
        <img
          class="navbar-brand-full d-md-down-none"
          :src="logo"
          height="45"
          style="max-width: 150px"
          alt="Logo"
          crossorigin="anonymous"
        />
        <img
          class="navbar-brand-full d-lg-none"
          :src="logo"
          height="45"
          alt="Logo"
          crossorigin="anonymous"
        />
        <img
          class="navbar-brand-minimized"
          :src="logoMinimized"
          style="max-width: 50px; max-height: 40px"
          alt="Logo"
          crossorigin="anonymous"
        />
      </b-link>
      <b-navbar-nav
        class="ml-auto header-nav d-md-down-none"
        style="margin-right: 150px"
      >
        <template v-for="item in nav">
          <b-nav-item
            v-if="!item.children && !item.hidden"
            :key="item.url"
            :to="item.url"
            class="mx-3"
          >
            {{ item.name }}
          </b-nav-item>
        </template>
        <b-button class="mx-3" variant="primary">{{
          $t("home.btn.report")
        }}</b-button>
      </b-navbar-nav>
      <b-navbar-nav class="ml-auto">
        <b-nav-item class="mr-2">
          <b-dropdown v-if="hasMultiLanguageSupport" variant="link">
            <template #button-content>
              <i
                class="flag-icon flag-icon-br"
                :class="{
                  'flag-icon-br': $i18n.locale === 'pt',
                  'flag-icon-es': $i18n.locale === 'es',
                  'flag-icon-us': $i18n.locale === 'en',
                }"
                style="font-size: 1.2rem"
                aria-hidden="true"
              />
            </template>
            <b-dropdown-item
              v-if="isLanguageAvailable('pt')"
              class="flag-item"
              @click="changeLocale('pt')"
            >
              <i class="flag-icon flag-icon-br" aria-hidden="true" /> Português
            </b-dropdown-item>
            <b-dropdown-item
              v-if="isLanguageAvailable('es')"
              class="flag-item"
              @click="changeLocale('es')"
            >
              <i class="flag-icon flag-icon-es" aria-hidden="true" /> Espanol
            </b-dropdown-item>
            <b-dropdown-item
              v-if="isLanguageAvailable('en')"
              class="flag-item"
              @click="changeLocale('en')"
            >
              <i class="flag-icon flag-icon-us" aria-hidden="true" /> English
            </b-dropdown-item>
          </b-dropdown>
        </b-nav-item>
      </b-navbar-nav>
    </AppHeader>
    <div class="app-body vertical">
      <AppSidebar v-if="layout.sidebar" fixed>
        <SidebarHeader />
        <SidebarNav :nav-items="nav"></SidebarNav>
        <SidebarFooter />
        <SidebarMinimizer @cui-sidebar-minimize="onMinimize" />
      </AppSidebar>
      <main class="main">
        <div class="container-fluid h-100">
          <template v-if="loading"> Carregando... </template>
          <router-view v-else></router-view>
        </div>
      </main>
    </div>
    <TheFooter class="vertical">
      <!--footer-->
      <div>
        {{ company.fantasyName }} - CNPJ
        {{
          company.cnpj.replace(
            /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
            "$1.$2.$3/$4-$5"
          )
        }}
      </div>
    </TheFooter>
  </div>
</template>

<script>
import {
  Header as AppHeader,
  SidebarToggler,
  Sidebar as AppSidebar,
  SidebarFooter,
  SidebarHeader,
  SidebarMinimizer,
  SidebarNav,
  Footer as TheFooter,
} from "@coreui/vue";

import { mapGetters } from "vuex";
import { Validator } from "vee-validate";
import { Routes } from "@/i18n";
import { getRoutes } from "@/router";

export default {
  name: "PortalContainer",
  components: {
    AppHeader,
    AppSidebar,
    TheFooter,
    SidebarFooter,
    SidebarToggler,
    SidebarHeader,
    SidebarNav,
    SidebarMinimizer,
  },
  beforeRouteLeave(to, from, next) {
    if (to.meta.modal) {
      next(false);
      this.$refs[to.meta.modal].show();
    } else {
      next();
    }
  },
  data() {
    return {
      nav: [],
      companyId: null,
      loading: false,
      minimized: false,
    };
  },
  computed: {
    ...mapGetters(["company", "portalBucket", "portal"]),
    routes() {
      return getRoutes();
    },
    name() {
      return this.$route.name;
    },
    list() {
      return this.$route.matched.filter(
        (route) => route.name || route.meta.label
      );
    },
    layout() {
      return (
        this.$route?.meta?.layout || {
          sidebar: true,
        }
      );
    },
    logo() {
      return `https://${this.portalBucket}/${this.portal._id}/${this.portal?.logo}`;
    },
    video() {
      return `https://${this.portalBucket}/${this.portal._id}/video.mp4`;
    },
    logoMinimized() {
      if (this.portal?.logoMinimized) {
        return `https://${this.portalBucket}/${this.portal._id}/${this.portal?.logoMinimized}`;
      }
      return null;
    },
    title() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].title
      )
        return portal.languages[lang].title;
      return this.$i18n.t("home.title", [this.company.fantasyName]);
    },
    companyClass() {
      return this.portal?.slug;
    },
    hasMultiLanguageSupport() {
      if (!this.portal) return false;
      if (!this.portal.languages) return false;
      let multiLanguage =
        this.portal.languages.pt && this.portal.languages.pt.enabled;
      multiLanguage =
        multiLanguage &&
        ((this.portal.languages.en && this.portal.languages.en.enabled) ||
          (this.portal.languages.es && this.portal.languages.es.enabled));

      return multiLanguage;
    },
  },
  watch: {
    "$i18n.locale": function () {
      this.updateNavigation();
    },
  },
  async created() {
    this.companyId = window.location.pathname
      .replace("/portal/", "")
      .replace(/\/+/g, "");
    document.title = `${this.company.fantasyName} - Portal de Compliance`;
    let link = document.querySelector("link[rel~='icon']");
    if (!link && this.portal.favicon) {
      link = document.createElement("link");
      link.rel = "icon";
      link.href = `https://${this.portalBucket}/${this.portal._id}/${this.portal.favicon}`;
      document.head.appendChild(link);
    }

    this.updateNavigation();
  },

  methods: {
    onMinimize(minimized) {
      this.minimized = minimized;
    },
    isLanguageAvailable(lang) {
      if (!this.portal) return false;
      if (!this.portal.languages) return false;
      if (!this.portal.languages[lang]) return false;
      return this.portal.languages[lang].enabled;
    },
    changeLocale(locale) {
      this.$i18n.locale = locale;
      Validator.localize(locale);
    },
    updateNavigation() {
      this.nav = [];
      const handleRoutes = (routes, parentPath = "") => {
        const items = [];

        for (const route of routes) {
          if (route.hidden || route.redirect) {
            if (route.children) {
              items.push(...handleRoutes(route.children));
            }
            continue;
          }

          const item = {
            name: this.$i18n.t(`router.${route.name}`),
            url: parentPath + "/" + Routes[route.path][this.$i18n.locale],
          };

          if (route.meta) {
            const meta = route.meta;
            item.hidden = meta.verticalHidden;
            if (meta.icon) {
              item.icon = meta.icon;
            }
            if (meta.module) {
              const module = this.company.modules[meta.module] || {};
              if (!module.enabled) {
                continue;
              }
              if (module.alternateForm) {
                item.name = this.$i18n.t(`router.${route.name}Alt`);
                item.url =
                  parentPath +
                  "/" +
                  Routes[`${route.path}Alt`][this.$i18n.locale];
              }
            }
          }

          if (route.children) {
            item.children = handleRoutes(route.children, route.path);
          }

          items.push(item);
        }
        return items;
      };

      const items = handleRoutes(this.routes);

      this.nav.push(...items);

      const filesRoute = [];
      const compliance = this.company.compliance;
      if (compliance && (compliance.files || compliance.oldFiles)) {
        let files = compliance.files;
        const hasOldFiles = Object.keys(compliance.oldFiles || {}).length > 0;
        if (files.length > 0) {
          // handle new way of files
          for (const file of files) {
            filesRoute.push({
              name: file.name,
              url: `/download/${file._id}`,
            });
          }
        } else if (hasOldFiles) {
          let files = compliance.oldFiles;
          if (files.policy) {
            filesRoute.push({
              name: this.$i18n.t("router.Compliance"),
              url: `/download/policy`,
            });
          }
          if (files.ethicsConduct) {
            if (
              ["pvax-md-gusto", "grupogurgacz"].indexOf(this.portal.slug) !== -1
            ) {
              //FIXME
              filesRoute.push({
                name: this.$i18n.t("router.Code of Ethics"),
                url: "/open/ethicsConduct",
              });
            } else if (this.portal.slug === "maxicredito") {
              filesRoute.push({
                name: "Pacto de Ética", //FIXME
                url: `/download/ethicsConduct`,
              });
            } else {
              filesRoute.push({
                name: this.$i18n.t("router.Code of Ethics"),
                url: `/download/ethicsConduct`,
              });
            }
          }
          if (files.procedures) {
            filesRoute.push({
              name: this.$i18n.t("router.Channel Policy"),
              url: `/download/procedures`,
            });
          }
          if (files.committee) {
            filesRoute.push({
              name: this.$i18n.t("router.Committee"),
              url: `/download/committee`,
            });
          }
        }
      }
      if (this.portal.slug === "maxicredito") {
        filesRoute.push({
          name: this.$i18n.t("router.Channel Video"),
          url: "/channel-video",
        });
      }

      if (filesRoute.length > 0) {
        this.nav.push({
          // title: true,
          name: this.$i18n.t("router.Information"),
          icon: "fas fa-building",
          children: filesRoute,
        });
      }
    },
  },
};
</script>
